'use client';

import { useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import {
  navigateWorkChatsInPlace,
  parseWorkChatsPath,
  type WorkChatsRoute,
} from './work-chats-navigation';

export type WorkChatsNavigation = {
  /** New history entry; in place inside the chats tree, else `router.push`. */
  push: (href: string) => void;
  /** Same entry (param cleanup, thread open/close); else `router.replace`. */
  replace: (href: string) => void;
};

/**
 * Stable navigation callbacks for anything that opens a chat. Uses Next's own
 * router for fallbacks: unlike the toploader wrapper it keeps one identity
 * across URL changes, so these callbacks are safe to hand to memoized rows.
 */
export function useWorkChatsNavigation(): WorkChatsNavigation {
  const router = useRouter();
  return useMemo(
    () => ({
      push: (href) => {
        if (!navigateWorkChatsInPlace(href, 'push')) {
          router.push(href);
        }
      },
      replace: (href) => {
        if (!navigateWorkChatsInPlace(href, 'replace')) {
          router.replace(href, { scroll: false });
        }
      },
    }),
    [router]
  );
}

/**
 * The Work Chats route for the current URL. Derived from `usePathname` (not
 * `useParams`): in-place history navigation keeps the originally matched
 * route segment mounted, so its params go stale while the pathname is current.
 */
export function useWorkChatsRoute(): WorkChatsRoute | null {
  const pathname = usePathname();
  return useMemo(() => parseWorkChatsPath(pathname ?? ''), [pathname]);
}
